import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Grid,
} from '@mui/material';
import Typo from './Typo';
import { t } from 'i18next';
import FloatingImages from './FloatingImages';

const Hero = ({ }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const partners = [
    { logo: '/assets/chekup/automod.png', name: t('heroPartnerAutoMod') },
    { logo: '/assets/chekup/pointS.png', name: t('heroPartnerPointS') },
    {
      logo: '/assets/chekup/vln-greyscale.png',
      name: t('heroPartnerVLN'),
    },
    {
      logo: '/assets/chekup/worldpac-greyscale.png',
      name: t('heroPartnerWorldPac'),
    },
  ];

  const handleButtonClick = () => {
    window.location.href = '';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        bgcolor: '#f8f9f9',
        paddingLeft: { xs: 5, md: 0 },
        paddingRight: { xs: 5, md: 0 },
        position: 'relative',
        pt: { xs: 8, md: 8 },
        pb: { xs: 0, md: 0 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: '1440px',
          px: { xs: 2, md: 4 },
        }}
      >
        <Grid container spacing={3}>
          {/* Left side - Main content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ py: { xs: 4, md: 5 }, maxWidth: 600 }}>
              <Typo
                variant="h1"
                text={t('heroTitle')}
                sx={{
                  mb: 1.5,
                }}
              />

              <Typo
                variant="h1"
                text={t('heroSubtitle')}
                sx={{
                  mb: 2.5,
                  color: '#bc0c0c',
                  fontWeight: 700,
                }}
              />

              <Typo
                variant="body1"
                text={t('heroDescription')}
                sx={{
                  mb: 5,
                  lineHeight: 1.6,
                  maxWidth: '540px',
                  fontSize: '17px',
                  color: '#2a2a2a',
                  fontWeight: 500,
                  fontFamily: 'Montserrat',
                }}
              />

              <Button
                variant="contained"
                onClick={handleButtonClick}
                sx={{
                  bgcolor: '#bc0c0c',
                  borderRadius: '21px',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(188, 12, 12, 0.2)',
                  '&:hover': {
                    bgcolor: '#a00a0a',
                    boxShadow: '0 6px 14px rgba(188, 12, 12, 0.25)',
                    transform: 'scale(1.04)',
                  },
                  mb: 4,
                  transition: 'transform 0.15s',
                }}
              >
                {t('heroCta')}
              </Button>


              <Box sx={{ mb: 5 }}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'block',
                    color: '#5d6267',
                    fontSize: '14px',
                    fontFamily: 'Montserrat',
                    fontWeight: '600',
                    lineHeight: '1.5',
                    wordWrap: 'break-word',
                    mb: 1.5,
                    textAlign: 'left',
                  }}
                >
                  {t('heroTrustedBy')}
                </Box>

                {/* Partners logos */}
                {partners && partners?.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Grid
                      container
                      spacing={1}
                      justifyContent="flex-start"
                      alignItems="center"
                    >
                      {partners?.map((partner, index) => (
                        <Grid item key={index}>
                          <Box
                            component="img"
                            src={partner.logo}
                            alt={partner.name}
                            sx={{
                              height: 'auto',
                              maxHeight: {
                                xs: 45,
                                md: 50,
                              },
                              width: 'auto',
                              maxWidth: {
                                xs: 100,
                                md: 110,
                              },
                              opacity: 0.8,
                              filter: 'grayscale(75%)',
                              transition: 'all 0.3s ease',
                              px: { xs: 1.5, md: 2 },
                              display: 'inline-block',
                              verticalAlign: 'middle',
                              '&:hover': {
                                opacity: 1,
                                filter: 'grayscale(0)',
                                transform: 'scale(1.05)',
                              },
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>

              {/* Subtle divider moved below partner section */}
              <Box
                sx={{
                  width: '100%',
                  height: 1,
                  bgcolor: '#e0e0e0',
                  mt: 1,
                  borderRadius: 1,
                  opacity: 0.7,
                  display: { xs: 'none', md: 'block' },
                }}
              />
            </Box>
          </Grid>

          {/* Right side - Hero Visual Image */}
          <Grid item xs={12} md={6} pt={5}>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                pt: 5,
                height: '100%',
                position: 'relative',
              }}
            >
              <FloatingImages />
              <Box
                component="img"
                src="/assets/chekup/heroChekUpComputer.png"
                alt={t('heroVisual')}
                sx={{
                  width: '100%',
                  maxWidth: 750,
                  borderRadius: '12px',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.15))',

                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
